import { endOfDay, startOfDay } from 'date-fns';
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { DATE_TIME_LOCALE } from './constants';

/**
 * Normalizes some inconsistencies in our allowed internal time zone values to ensure they will
 * work properly when using date-related APIs. Will return default of "local" if time zone
 * not specified.
 */
export function normalizeTimeZone(timeZone: string | undefined = 'local') {
  return timeZone === 'utc' ? 'UTC' : timeZone;
}

/**
 * Normalizes our allowed internal time zone values into an IANA time zone value, which is necessary
 * for some time zone formatting utilities.
 */
export function normalizeIANATimeZone(timeZone: string | undefined = 'local') {
  const normalizedTimeZone = normalizeTimeZone(timeZone);

  if (isLocalTimeZone(normalizedTimeZone)) {
    return getLocalIANATimeZone();
  }

  return normalizedTimeZone;
}

/**
 * Returns true if the specified time zone value indicates it is using the user's
 * local, browser-based time zone. If not specified, the time zone is assumed to be
 * local.
 */
export function isLocalTimeZone(timeZone?: string) {
  const lowerTimeZone = normalizeTimeZone(timeZone).toLowerCase();
  return lowerTimeZone === 'local' || lowerTimeZone === 'browser';
}

/**
 * Get the local browser time zone in IANA format (e.g. America/New_York).
 */
export function getLocalIANATimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns the time zone offset from UTC in milliseconds.
 */
export function getTimeZoneOffsetMs(timeZone?: string) {
  const normalizedTimeZone = normalizeIANATimeZone(timeZone);
  const now = new Date();
  return getTimezoneOffset(normalizedTimeZone, now);
}

export interface TimeZoneNameOpts {
  /**
   * Format for the time zone name. See Intl/DateTimeFormat docs for more details.
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#timezonename
   *
   * @default 'short'
   */
  variant?: 'short' | 'long';
}

/**
 * Returns the name of the time zone.
 */
export function getTimeZoneName(timeZone?: string, { variant = 'short' }: TimeZoneNameOpts = {}) {
  const normalizedTimeZone = normalizeIANATimeZone(timeZone);

  const parts = Intl.DateTimeFormat(DATE_TIME_LOCALE, {
    timeZone: normalizedTimeZone,
    timeZoneName: variant,
  }).formatToParts(new Date());

  return parts.find((part) => part.type === 'timeZoneName')?.value;
}

/**
 * Interprets the given date's local date/time in the given timezone and returns
 * the corresponding UTC date.
 */
export function interpretDateInTimeZone(date: Date, timeZone: string | undefined): Date {
  if (isLocalTimeZone(timeZone)) {
    return date;
  }
  return zonedTimeToUtc(date, normalizeIANATimeZone(timeZone));
}

/**
 * Converts a UTC date to the same calendar day (in the given timezone) at start of day
 * in the browser's local time. Used for the date calendar as the calendar uses local getDate
 * to determine what day is selected
 *
 * Uses the calendar day in the target timezone (not the browser's), so that when the
 * browser is in a different zone we still pass the correct day to the calendar
 */
export function zonedDateToLocalStartOfDay(date: Date, timeZone: string | undefined): Date {
  if (isLocalTimeZone(timeZone)) {
    return startOfDay(date);
  }
  const normalizedTimeZone = normalizeIANATimeZone(timeZone);
  const zoned = utcToZonedTime(date, normalizedTimeZone);
  const y = zoned.getFullYear();
  const m = zoned.getMonth();
  const d = zoned.getDate();
  return new Date(y, m, d, 0, 0, 0, 0);
}

/**
 * Converts a date to start or end of that calendar day in the display timezone.
 *
 * We accommodate both UTC and local dates so that when selecting the end of a range, the start date
 * (which has already been converted to UTC start of day so the calendar grid is correctly highlighted)
 * will not shift.
 */
export function calendarClickToZonedDate(date: Date, timeZone: string | undefined, useEndOfDay: boolean): Date {
  if (isLocalTimeZone(timeZone)) {
    return useEndOfDay ? endOfDay(date) : startOfDay(date);
  }
  const normalizedTimeZone = normalizeIANATimeZone(timeZone);
  const isUTCStartOfDay = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
  const y = isUTCStartOfDay ? date.getUTCFullYear() : date.getFullYear();
  const m = isUTCStartOfDay ? date.getUTCMonth() : date.getMonth();
  const d = isUTCStartOfDay ? date.getUTCDate() : date.getDate();
  const time = useEndOfDay ? [23, 59, 59, 999] : [0, 0, 0, 0];
  const localDate = new Date(y, m, d, ...time);
  return zonedTimeToUtc(localDate, normalizedTimeZone);
}

// Looks for the digits of an offset.
const TZ_OFFSET_REGEX = /([+-]?)(\d{1,2})(:\d{2})?/;

/**
 * Attempts to parse a string-based long time zone offset (e.g. +08:00, +00:30) from a time zone
 * name. Returns undefined if not able to determine an offset. Useful in some rare parsing cases
 * where we need to do some date math based on a string-based offset name.
 *
 * Intentionally organizing into the long format, which is compatible with the date-fns-tz lib we
 * use for time zone related utilities.
 */
export function getOffsetStrFromTimeZoneName(timeZoneStr: string) {
  if (!timeZoneStr) {
    return;
  }

  if (timeZoneStr === 'GMT' || timeZoneStr === 'UTC') {
    return 'UTC';
  }

  // If we can parse a likely offset from the string, return it as is.
  const offsetMatch = timeZoneStr.match(TZ_OFFSET_REGEX);
  if (offsetMatch && offsetMatch[2]) {
    const [_, signStr, hourStr, minuteStr] = offsetMatch;
    const sign = signStr || '';
    // Ensure hour is two digits.
    const hour = hourStr.padStart(2, '0');
    // Fall back to :00 if not specified.
    const minute = minuteStr || ':00';

    return `${sign}${hour}${minute}`;
  }

  // If offset not found, attempt to pull from the Intl API. Useful for covering the 3-letter
  // time zone abbreviations like PST.
  try {
    const now = new Date();
    const timeZoneParts = Intl.DateTimeFormat(DATE_TIME_LOCALE, {
      timeZone: timeZoneStr.replace('GMT', ''),
      timeZoneName: 'longOffset',
    }).formatToParts(now);

    const timeZoneOffset = timeZoneParts.find((part) => part.type === 'timeZoneName')?.value;
    if (timeZoneOffset === 'GMT') {
      return 'UTC';
    }

    if (timeZoneOffset) {
      // Remove GMT prefix to just leave behind the offset.
      return timeZoneOffset.replace('GMT', '');
    }
  } catch (_e) {
    // Do nothing. Invalid time zone.
  }
}

export function isStartOfDayInTimeZone(date: Date, timeZone?: string): boolean {
  const zonedDate = timeZone !== undefined && timeZone !== 'local' ? utcToZonedTime(date, timeZone) : date;
  const isStartOfDay = zonedDate.getHours() === 0 && zonedDate.getMinutes() === 0 && zonedDate.getSeconds() === 0;
  return isStartOfDay;
}
